/**
 * Schema Generator - JSON-LD structured data generator tool.
 *
 * Provides form-based input for 10 schema.org types with
 * live JSON-LD preview and copy-to-clipboard functionality.
 */

import { useState, useMemo, useCallback } from "react";
import {
  Building2,
  FileText,
  ShoppingBag,
  HelpCircle,
  ListOrdered,
  MapPin,
  CalendarDays,
  List,
  Briefcase,
  Wrench,
  Copy,
  Check,
  Plus,
  Trash2,
  Code2,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SCHEMA_TYPES, type SchemaTypeConfig, type SchemaField } from "./schemaTypes";

const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  Building2,
  FileText,
  ShoppingBag,
  HelpCircle,
  ListOrdered,
  MapPin,
  CalendarDays,
  List,
  Briefcase,
  Wrench,
};

// --- Type Selector Grid ---

function TypeSelectorGrid({
  selectedType,
  onSelect,
}: {
  selectedType: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground mb-4">Select Schema Type</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {SCHEMA_TYPES.map((schemaType) => {
          const IconComponent = ICON_MAP[schemaType.icon];
          const isSelected = selectedType === schemaType.id;
          return (
            <button
              key={schemaType.id}
              onClick={() => onSelect(schemaType.id)}
              className={`
                flex flex-col items-start gap-3 p-4 rounded-xl border-2 text-left transition-all duration-200
                hover:shadow-md cursor-pointer
                ${isSelected
                  ? "border-orange-500 bg-orange-50 dark:bg-orange-950/40/50 shadow-sm"
                  : "border-border bg-card hover:border-border"
                }
              `}
            >
              <div className={`p-2.5 rounded-xl ${schemaType.color}`}>
                {IconComponent && <IconComponent className="w-5 h-5" />}
              </div>
              <div>
                <div className={`text-sm font-semibold ${isSelected ? "text-foreground" : "text-foreground"}`}>
                  {schemaType.name}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{schemaType.description}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// --- Array Field Component ---

function ArrayFieldInput({
  field,
  value,
  onChange,
}: {
  field: SchemaField;
  value: Array<Record<string, string>>;
  onChange: (val: Array<Record<string, string>>) => void;
}) {
  const addItem = () => {
    const emptyItem: Record<string, string> = {};
    for (const f of field.arrayFields || []) {
      emptyItem[f.key] = "";
    }
    onChange([...value, emptyItem]);
  };

  const removeItem = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, key: string, val: string) => {
    const updated = [...value];
    updated[index] = { ...updated[index], [key]: val };
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">
          {field.label}
          {field.required && <span className="text-red-500 dark:text-red-400 ml-0.5">*</span>}
        </Label>
        <button
          onClick={addItem}
          className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 hover:text-orange-700 font-medium"
        >
          <Plus className="w-3.5 h-3.5" />
          Add {field.arrayItemLabel || "Item"}
        </button>
      </div>
      {value.length === 0 && (
        <p className="text-xs text-muted-foreground italic">
          No items added yet. Click "Add {field.arrayItemLabel || "Item"}" to start.
        </p>
      )}
      {value.map((item, index) => (
        <div key={index} className="relative p-4 bg-muted rounded-lg border border-border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              {field.arrayItemLabel || "Item"} {index + 1}
            </span>
            <button
              onClick={() => removeItem(index)}
              className="text-muted-foreground hover:text-red-500 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="space-y-3">
            {(field.arrayFields || []).map((subField) => (
              <div key={subField.key}>
                <Label className="text-xs font-medium text-foreground">
                  {subField.label}
                  {subField.required && <span className="text-red-500 dark:text-red-400 ml-0.5">*</span>}
                </Label>
                {subField.type === "textarea" ? (
                  <Textarea
                    value={item[subField.key] || ""}
                    onChange={(e) => updateItem(index, subField.key, e.target.value)}
                    placeholder={subField.placeholder}
                    className="mt-1.5 text-sm min-h-[60px] bg-card"
                  />
                ) : (
                  <Input
                    value={item[subField.key] || ""}
                    onChange={(e) => updateItem(index, subField.key, e.target.value)}
                    placeholder={subField.placeholder}
                    className="mt-1.5 text-sm bg-card"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Form Field Renderer ---

function FormField({
  field,
  value,
  onChange,
}: {
  field: SchemaField;
  value: unknown;
  onChange: (val: unknown) => void;
}) {
  if (field.type === "array") {
    return (
      <ArrayFieldInput
        field={field}
        value={(value as Array<Record<string, string>>) || []}
        onChange={onChange}
      />
    );
  }

  return (
    <div>
      <Label className="text-sm font-medium text-foreground">
        {field.label}
        {field.required && <span className="text-red-500 dark:text-red-400 ml-0.5">*</span>}
      </Label>
      {field.type === "textarea" ? (
        <Textarea
          value={(value as string) || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className="mt-1.5 bg-card"
        />
      ) : field.type === "select" ? (
        <select
          value={(value as string) || field.options?.[0]?.value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1.5 flex h-10 w-full rounded-md border border-border hover:border-border transition-colors bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/20 focus-visible:border-orange-500"
        >
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <Input
          type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
          value={(value as string) || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className="mt-1.5 bg-card"
          step={field.type === "number" ? "any" : undefined}
        />
      )}
    </div>
  );
}

// --- Schema Form ---

function SchemaForm({
  schemaType,
  formData,
  onFieldChange,
  validationErrors,
}: {
  schemaType: SchemaTypeConfig;
  formData: Record<string, unknown>;
  onFieldChange: (key: string, value: unknown) => void;
  validationErrors: string[];
}) {
  const IconComponent = ICON_MAP[schemaType.icon];

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <Sparkles className="w-4 h-4 text-orange-500 dark:text-orange-400" />
        <h3 className="text-sm font-semibold text-foreground">
          {schemaType.name} Details
        </h3>
      </div>

      {validationErrors.length > 0 && (
        <div className="mb-5 p-3 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 dark:text-red-400 mt-0.5 shrink-0" />
            <div className="text-sm text-red-700 dark:text-red-400">
              {validationErrors.map((err, i) => (
                <div key={i}>{err}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {schemaType.fields.map((field) => (
          <FormField
            key={field.key}
            field={field}
            value={formData[field.key]}
            onChange={(val) => onFieldChange(field.key, val)}
          />
        ))}
      </div>
    </div>
  );
}

// --- JSON Preview Panel ---

function JsonPreviewPanel({ jsonLd }: { jsonLd: Record<string, unknown> }) {
  const [copied, setCopied] = useState(false);

  const jsonString = useMemo(() => {
    return JSON.stringify(jsonLd, null, 2);
  }, [jsonLd]);

  const scriptTag = useMemo(() => {
    return `<script type="application/ld+json">\n${jsonString}\n</script>`;
  }, [jsonString]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(scriptTag);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for non-HTTPS contexts
      const textarea = document.createElement("textarea");
      textarea.value = scriptTag;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [scriptTag]);

  // Simple syntax highlighting - match reference design colors
  const highlightedJson = useMemo(() => {
    return jsonString
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"([^"]+)":/g, '<span class="text-teal">"$1"</span>:')
      .replace(/: "([^"]*)"/g, ': <span class="text-green-400">"$1"</span>')
      .replace(/: (\d+\.?\d*)/g, ': <span class="text-blue-400">$1</span>')
      .replace(/: (true|false|null)/g, ': <span class="text-orange-400">$1</span>');
  }, [jsonString]);

  return (
    <div className="sticky top-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-foreground">
          Generated Schema
        </h3>
        <button
          onClick={handleCopy}
          className={`
            flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all
            ${copied
              ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700"
              : "bg-muted text-foreground border border-border hover:bg-gray-200"
            }
          `}
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copied!" : "Copy Code"}
        </button>
      </div>
      <div className="relative rounded-xl bg-gray-950 border border-gray-800 overflow-hidden shadow-lg">
        {/* macOS-style window dots */}
        <div className="px-4 py-3 bg-gray-900 border-b border-gray-800 flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
          </div>
          <span className="text-xs text-muted-foreground ml-3 font-mono">application/ld+json</span>
        </div>
        <pre className="p-4 overflow-x-auto text-sm leading-relaxed max-h-[600px] overflow-y-auto">
          <code
            className="text-gray-300 font-mono"
            dangerouslySetInnerHTML={{ __html: highlightedJson }}
          />
        </pre>
      </div>
    </div>
  );
}

// --- Main Component ---

export default function SchemaGenerator() {
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [formDataMap, setFormDataMap] = useState<Record<string, Record<string, unknown>>>({});

  const selectedType = useMemo(
    () => SCHEMA_TYPES.find((t) => t.id === selectedTypeId) || null,
    [selectedTypeId],
  );

  const currentFormData = selectedTypeId ? formDataMap[selectedTypeId] || {} : {};

  const handleFieldChange = useCallback(
    (key: string, value: unknown) => {
      if (!selectedTypeId) return;
      setFormDataMap((prev) => ({
        ...prev,
        [selectedTypeId]: {
          ...prev[selectedTypeId],
          [key]: value,
        },
      }));
    },
    [selectedTypeId],
  );

  const handleTypeSelect = useCallback((id: string) => {
    setSelectedTypeId(id);
    // Initialize array fields with one empty item if none exist
    setFormDataMap((prev) => {
      if (prev[id]) return prev;
      const schemaType = SCHEMA_TYPES.find((t) => t.id === id);
      if (!schemaType) return prev;
      const initialData: Record<string, unknown> = {};
      for (const field of schemaType.fields) {
        if (field.type === "array") {
          const emptyItem: Record<string, string> = {};
          for (const f of field.arrayFields || []) {
            emptyItem[f.key] = "";
          }
          initialData[field.key] = [emptyItem];
        } else if (field.type === "select" && field.options?.[0]) {
          initialData[field.key] = field.options[0].value;
        }
      }
      return { ...prev, [id]: initialData };
    });
  }, []);

  // Validate required fields
  const validationErrors = useMemo(() => {
    if (!selectedType) return [];
    const errors: string[] = [];
    for (const field of selectedType.fields) {
      if (field.required && field.type !== "array") {
        const val = currentFormData[field.key];
        if (!val || (typeof val === "string" && val.trim() === "")) {
          errors.push(`${field.label} is required`);
        }
      }
    }
    return errors;
  }, [selectedType, currentFormData]);

  // Build the JSON-LD
  const jsonLd = useMemo(() => {
    if (!selectedType) return null;
    return selectedType.buildJsonLd(currentFormData);
  }, [selectedType, currentFormData]);

  return (
    <div className="space-y-6">
      {/* Header - Orange gradient with code icon */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-400 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-card/20 rounded-xl">
            <Code2 className="w-7 h-7" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Schema Generator</h2>
            <p className="text-orange-50 text-sm mt-0.5">
              Generate JSON-LD structured data for rich search results
            </p>
          </div>
        </div>
      </div>

      {/* Type Selector */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <TypeSelectorGrid selectedType={selectedTypeId} onSelect={handleTypeSelect} />
      </div>

      {/* Form + Preview */}
      {selectedType && jsonLd && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Form Panel */}
          <div className="bg-card rounded-2xl border border-border p-6">
            <SchemaForm
              schemaType={selectedType}
              formData={currentFormData}
              onFieldChange={handleFieldChange}
              validationErrors={validationErrors}
            />
          </div>
          
          {/* Right: JSON Preview Panel */}
          <div>
            <JsonPreviewPanel jsonLd={jsonLd} />
          </div>
        </div>
      )}

      {/* Empty state */}
      {!selectedType && (
        <div className="text-center py-16 text-muted-foreground bg-card rounded-2xl border border-border border-dashed">
          <Code2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-sm">Select a schema type above to get started</p>
        </div>
      )}
    </div>
  );
}
